import { useMutation, useQuery } from 'react-query';
import transactionsServices from './transactions.services';
import notificationError from '@/utils/functions/notificationError';
import { AxiosError } from 'axios';
import { addRequestToQueue } from '@/utils/tefRequestsIndexedDB';
import moment from 'moment';
import { Modal, notification } from 'antd';
import {
  PaymentFinancialPendingRequest,
  PaymentFinancialPendingStatus,
  TransactionEntityType,
} from './transactions.types';
import { createElement } from 'react';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { getUserFromLocalStorage } from '@/utils/user';

export function usePaymentTransactionCreate() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: transactionsServices.create,
    onSuccess() {
      notification.success({ message: 'Pagamento processado com sucesso!' });

      queryChanel({
        invalidateQueries: [['payment-trasaction/financial-pending']],
      });
    },
    async onError(error: AxiosError, vars) {
      notificationError(error);

      if (error.isAxiosError) {
        try {
          const { data, baseURL, url, headers, method } = error.config;

          const type = {
            [TransactionEntityType.Sale]: 'Venda:',
            [TransactionEntityType.WorkOrder]: 'OS:',
          };

          await addRequestToQueue(
            JSON.stringify({
              description: `Criação do financeiro • ${
                type[vars.entityType]
              } ${vars.entityCode?.replace('O.S. ', '')}`,
              createdAt: moment().format(),
              data,
              baseURL: baseURL || process.env.REACT_APP_API_URL,
              url,
              headers,
              method,
              refreshToken: getUserFromLocalStorage().refreshToken,
              refreshAuthorization: `Basic ${btoa(
                `${process.env.REACT_APP_CLIENT_ID}:${process.env.REACT_APP_CLIENT_PASSWORD}`,
              )}`,
            }),
          );

          Modal.confirm({
            icon: null,
            title: 'Erro na geração do financeiro',
            width: 500,
            content: createElement(
              'div',
              null,
              createElement(
                'p',
                { style: { fontWeight: '600' } },
                '✅ Fique tranquilo! O pagamento da maquininha foi processado com sucesso.',
              ),
              createElement(
                'p',
                null,
                '⏳ Sua solicitação de criação do financeiro já está salva e será processada em breve. Assim que concluída, você receberá uma notificação.',
              ),
              createElement(
                'p',
                null,
                '👉 Para acompanhar o status, acesse o botão flutuante no canto inferior esquerdo do sistema.',
              ),
            ),
            cancelText: 'Fechar',
            okButtonProps: { style: { display: 'none' } },
          });
        } catch (err) {
          console.error('TEF_QUEUE_ERROR:', err);
        }
      }
    },
  });
}

export function usePaymentStatus(
  params: PaymentFinancialPendingRequest,
  enabled = true,
  refetch?: (status: PaymentFinancialPendingStatus) => void,
) {
  return useQuery({
    queryKey: ['payment-trasaction/financial-pending', { ...params }],
    queryFn: () => transactionsServices.status(params),
    onSuccess(data) {
      refetch?.(data.status);
    },
    onError(error: AxiosError) {
      if (error.isAxiosError && error.response?.status !== 404) {
        notificationError(error);
      }
    },
    enabled: !!params.entityId && !!params.entityType && enabled,
    refetchInterval: 15000,
  });
}

export function usePaymentTransactionCancel() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: transactionsServices.transactionCancel,
    onSuccess() {
      notification.success({ message: 'Cancelamento processado com sucesso!' });

      queryChanel({
        invalidateQueries: [['payment-trasaction/financial-pending']],
      });
    },
    async onError(error: AxiosError, vars) {
      notificationError(error);

      if (error.isAxiosError) {
        try {
          const { data, baseURL, url, headers, method } = error.config;

          await addRequestToQueue(
            JSON.stringify({
              description: `Cancelamento do financeiro • Conta a receber - ${vars.code}`,
              createdAt: moment().format(),
              data,
              baseURL: baseURL || process.env.REACT_APP_API_URL,
              url,
              headers,
              method,
              refreshToken: getUserFromLocalStorage().refreshToken,
              refreshAuthorization: `Basic ${btoa(
                `${process.env.REACT_APP_CLIENT_ID}:${process.env.REACT_APP_CLIENT_PASSWORD}`,
              )}`,
            }),
          );

          Modal.confirm({
            icon: null,
            title: 'Erro no cancelamento do financeiro',
            width: 500,
            content: createElement(
              'div',
              null,
              createElement(
                'p',
                { style: { fontWeight: '600' } },
                '✅ Fique tranquilo! O cancelamento do pagamento foi processado com sucesso.',
              ),
              createElement(
                'p',
                null,
                '⏳ Sua solicitação de cancelamento do financeiro já está salva e será processada em breve. Assim que concluída, você receberá uma notificação.',
              ),
              createElement(
                'p',
                null,
                '👉 Para acompanhar o status, acesse o botão flutuante no canto inferior esquerdo do sistema.',
              ),
            ),
            cancelText: 'Fechar',
            okButtonProps: { style: { display: 'none' } },
          });
        } catch (err) {
          console.error('TEF_QUEUE_ERROR:', err);
        }
      }
    },
  });
}
